class NavigationHistoryService {
  private historyStack: string[] = [];
  private currentIndex = -1;
  private readonly STORAGE_KEY = 'navigation_history';

  constructor() {
    this.loadFromStorage();
    if (typeof window !== 'undefined' && this.historyStack.length === 0) {
      this.historyStack = [window.location.pathname];
      this.currentIndex = 0;
      this.saveToStorage();
    }
  }

  private loadFromStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        this.historyStack = data.historyStack || [];
        this.currentIndex = data.currentIndex || -1;
      }
    } catch {
      this.historyStack = [];
      this.currentIndex = -1;
    }
  }

  private saveToStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(
        this.STORAGE_KEY,
        JSON.stringify({
          historyStack: this.historyStack,
          currentIndex: this.currentIndex,
        }),
      );
    } catch {
      // Storage might be full or unavailable
    }
  }

  push(path: string): void {
    if (this.historyStack[this.currentIndex] === path) {
      return;
    }

    if (this.historyStack.length >= 2) {
      const previousPath = this.historyStack[this.currentIndex - 1];

      if (path === previousPath) {
        this.currentIndex--;
        this.saveToStorage();
        return;
      }

      if (
        this.currentIndex < this.historyStack.length - 1 &&
        this.historyStack[this.currentIndex + 1] === path
      ) {
        this.currentIndex++;
        this.saveToStorage();
        return;
      }
    }

    this.historyStack = this.historyStack.slice(0, this.currentIndex + 1);
    this.historyStack.push(path);
    this.currentIndex = this.historyStack.length - 1;
    this.saveToStorage();
  }

  goBack(): string | null {
    if (this.currentIndex > 0) {
      this.historyStack.splice(this.currentIndex, 1);
      this.currentIndex--;
      const previousPath = this.historyStack[this.currentIndex];
      this.saveToStorage();
      return previousPath || null;
    }
    return null;
  }

  goForward(): string | null {
    if (this.currentIndex < this.historyStack.length - 1) {
      this.currentIndex++;
      const nextPath = this.historyStack[this.currentIndex];
      this.saveToStorage();
      return nextPath || null;
    }
    return null;
  }

  getCurrentPath(): string {
    return this.historyStack[this.currentIndex] || '/';
  }

  getPreviousPath(): string | null {
    return this.currentIndex > 0
      ? this.historyStack[this.currentIndex - 1] || null
      : null;
  }

  canGoBack(): boolean {
    return this.currentIndex > 0;
  }

  canGoForward(): boolean {
    return this.currentIndex < this.historyStack.length - 1;
  }

  getHistoryStack(): string[] {
    return [...this.historyStack];
  }

  clear(): void {
    this.historyStack = [];
    this.currentIndex = -1;
    this.saveToStorage();
  }

  getStackSize(): number {
    return this.historyStack.length;
  }
}

export const navigationHistory = new NavigationHistoryService();
export default navigationHistory;
